/** Main 3-panel shell: sidebar | editor | metadata */

import { useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Document } from '@agentwiki/shared'
import { useAppStore } from '../../stores/app-store'
import { buildCreateDocumentBody, useCreateDocument } from '../../hooks/use-documents'
import { useKeyboardShortcuts, type ShortcutDefinition } from '../../hooks/use-keyboard-shortcuts'
import { useIsMobile } from '../../hooks/use-is-mobile'
import { useIsRailDrawer } from '../../hooks/use-is-rail-drawer'
import { useBodyScrollLock } from '../../hooks/use-body-scroll-lock'
import { useSwipeGesture } from '../../hooks/use-swipe-gesture'
import { usePreferenceSync } from '../../hooks/use-preference-sync'
import { Sidebar } from './sidebar'
import { MainPanel } from './main-panel'
import { MetadataPanel } from './metadata-panel'
import { StorageDrawer } from '../storage/storage-drawer'
import { cn } from '../../lib/utils'
import { apiClient } from '../../lib/api-client'
import { getWebFeatureProfile } from '../../lib/feature-flags'
import { documentEditPath } from '../../lib/document-route'
import { ResponsiveEditorDrawerLayout } from './responsive-editor-drawer-layout'

export function Layout({
  document: initialDocument,
  canCreate,
}: {
  document: Document
  canCreate: boolean
}) {
  const storageEnabled = getWebFeatureProfile().storageSpace
  const {
    theme,
    openTab,
    setActiveTab,
    openTabs,
    activeTabId,
    sidebarCollapsed,
    setSidebarCollapsed,
    metadataPanelCollapsed,
    setMetadataPanelCollapsed,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    mobileMetadataOpen,
    setMobileMetadataOpen,
  } = useAppStore()
  const navigate = useNavigate()
  const createDocument = useCreateDocument()
  const isMobile = useIsMobile()
  const drawerMode = useIsRailDrawer()
  useBodyScrollLock(drawerMode && (mobileSidebarOpen || mobileMetadataOpen))

  // Sync expanded folders + open tabs to DB
  usePreferenceSync()

  // Edge-swipe gestures for mobile drawer open/close (edge-only to avoid editor conflicts)
  useSwipeGesture({
    onSwipeRight: () => setMobileSidebarOpen(true),
    onSwipeLeft: () => setMobileMetadataOpen(true),
    onBackdropSwipeLeft: () => setMobileSidebarOpen(false), // swipe left on backdrop → close sidebar
    onBackdropSwipeRight: () => setMobileMetadataOpen(false), // swipe right on backdrop → close metadata
    enabled: isMobile,
  })

  // Hydrate tab from URL slug
  useEffect(() => {
    const tabId = `tab-${initialDocument.id}`
    const alreadyOpen = openTabs.some((t) => t.documentId === initialDocument.id)
    if (!alreadyOpen) {
      openTab({ id: tabId, documentId: initialDocument.id, title: initialDocument.title })
    }
    setActiveTab(tabId)
  }, [initialDocument, openTab, setActiveTab, openTabs])

  // Close mobile drawers on resize to desktop
  useEffect(() => {
    if (!drawerMode) {
      setMobileSidebarOpen(false)
      setMobileMetadataOpen(false)
    }
  }, [drawerMode, setMobileSidebarOpen, setMobileMetadataOpen])

  // Keyboard shortcuts
  const handleNewDoc = useCallback(async () => {
    if (!canCreate) return
    const doc = await createDocument.mutateAsync(
      buildCreateDocumentBody({
        title: 'Untitled',
        source: 'the AgentWiki new-document keyboard shortcut',
      }),
    )
    const tabId = `tab-${doc.id}`
    openTab({ id: tabId, documentId: doc.id, title: doc.title })
    setActiveTab(tabId)
    navigate(documentEditPath(doc))
  }, [canCreate, createDocument, openTab, setActiveTab, navigate])

  const shortcuts = useMemo<ShortcutDefinition[]>(
    () => [
      ...(canCreate ? [{ keys: 'ctrl+n', action: handleNewDoc }] : []),
      { keys: 'ctrl+\\', action: () => setSidebarCollapsed(!sidebarCollapsed) },
      { keys: 'ctrl+.', action: () => setMetadataPanelCollapsed(!metadataPanelCollapsed) },
      {
        keys: 'ctrl+s',
        action: async () => {
          const activeTab = openTabs.find((t) => t.id === activeTabId)
          if (!activeTab) return
          try {
            await apiClient.post(`/api/documents/${activeTab.documentId}/versions`)
          } catch {}
        },
      },
      {
        keys: 'ctrl+shift+[',
        action: () => {
          const idx = openTabs.findIndex((t) => t.id === activeTabId)
          if (idx > 0) {
            setActiveTab(openTabs[idx - 1].id)
            navigate(documentEditPath({ id: openTabs[idx - 1].documentId }))
          }
        },
      },
      {
        keys: 'ctrl+shift+]',
        action: () => {
          const idx = openTabs.findIndex((t) => t.id === activeTabId)
          if (idx < openTabs.length - 1) {
            setActiveTab(openTabs[idx + 1].id)
            navigate(documentEditPath({ id: openTabs[idx + 1].documentId }))
          }
        },
      },
    ],
    [
      handleNewDoc,
      canCreate,
      sidebarCollapsed,
      metadataPanelCollapsed,
      openTabs,
      activeTabId,
      setSidebarCollapsed,
      setMetadataPanelCollapsed,
      setActiveTab,
      navigate,
    ],
  )

  useKeyboardShortcuts(shortcuts)

  if (drawerMode) {
    return (
      <ResponsiveEditorDrawerLayout
        theme={theme}
        storageEnabled={storageEnabled}
        documentNavigationOpen={mobileSidebarOpen}
        metadataOpen={mobileMetadataOpen}
        setDocumentNavigationOpen={setMobileSidebarOpen}
        setMetadataOpen={setMobileMetadataOpen}
        canCreate={canCreate}
      />
    )
  }

  return (
    <div
      className={cn(
        'flex h-full overflow-hidden font-sans antialiased',
        theme === 'dark' ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900',
      )}
    >
      <Sidebar canCreate={canCreate} />
      <MainPanel />
      <MetadataPanel />
      {storageEnabled && <StorageDrawer />}
    </div>
  )
}
